import { CubePresenter, FlipIntent, Logger, NOOP_VOID, ScopeSlot } from 'src/3rds/cube'
import { MainPresenter } from 'src/main/Main.presenter'
import { AppStorage } from 'src/utils'
import { ServiceError } from 'src/utils/ServiceError'
import { BloqueiosKeys } from './Bloqueios.keys'
import { Bloqueio, BLOQUEIOS_POR_PAGINA, BloqueiosScope, MOTIVO_MINIMO } from './Bloqueios.scopes'
import { BloqueiosService } from './Bloqueios.service'

const LOG = Logger.get('BloqueiosPresenter')
const service = BloqueiosService.singleton()
const appStorage = AppStorage.singleton()

// Espera o operador parar de digitar antes de consultar o servidor.
const DEBOUNCE_MS = 300

// Bloquear corta o WhatsApp da LIA para o número, nos dois sentidos: só admin, como no servidor.
export const ROLES_AUTORIZADOS = new Set(['admin'])

export function podeBloquearNumeros() {
    return ROLES_AUTORIZADOS.has(`${appStorage.userRole ?? ''}`)
}

export function motivoSuficiente(motivo: string) {
    return motivo.replace(/\s+/g, ' ').trim().length >= MOTIVO_MINIMO
}

/** 5548999998888 → +55 (48) 99999-8888 */
export function formatarTelefone(telefone: string): string {
    const d = telefone.replace(/\D/g, '')
    const m = d.match(/^55(\d{2})(\d{4,5})(\d{4})$/)
    return m ? `+55 (${m[1]}) ${m[2]}-${m[3]}` : `+${d}`
}

/** O telefone digitado tem cara de telefone com DDD? (o servidor confere de novo) */
export function telefoneValido(texto: string): boolean {
    const d = texto.replace(/\D/g, '')
    return d.length >= 10 && d.length <= 13
}

/** Aviso depois de bloquear, conforme o que a Meta respondeu. */
export function avisoDoBloqueio(b: Bloqueio): { severity: 'success' | 'warning'; texto: string } {
    const numero = formatarTelefone(b.telefone)
    switch (b.metaSituacao) {
        case 'bloqueado':
            return { severity: 'success', texto: `${numero} bloqueado no servidor e na Meta.` }
        case 'simulado':
            return { severity: 'success', texto: `${numero} bloqueado (Meta simulada no desenvolvimento).` }
        default:
            return {
                severity: 'warning',
                texto: `${numero} bloqueado no servidor, mas a Meta recusou: ${b.metaErro ?? 'sem detalhe'}`
            }
    }
}

function mensagemDeNegocio(error: unknown): string | undefined {
    if (error instanceof ServiceError && [400, 404].includes(error.statusCode)) {
        return error.message
    }
    return undefined
}

export class BloqueiosPresenter extends CubePresenter<MainPresenter, BloqueiosScope> {
    private parentSlot: ScopeSlot = NOOP_VOID
    private buscaTimer?: ReturnType<typeof setTimeout>

    constructor(app: MainPresenter) {
        super(app, new BloqueiosScope())
    }

    public override release() {
        if (this.buscaTimer) clearTimeout(this.buscaTimer)
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new BloqueiosKeys(this.app, intent)

        if (initialization) {
            if (!podeBloquearNumeros()) {
                this.app.alert('warning', 'Acesso restrito', 'O bloqueio de números é restrito a administradores.')
                keys.allow = false
                return keys.allow
            }
            this.parentSlot = keys.parentSlot
            this.#initialize()
            await this.#carregarTudo()
        }

        this.parentSlot(this.scope)
        return keys.allow
    }

    #initialize() {
        const { scope } = this
        scope.onAtualizar = this.action(this.#carregarTudo, this)
        scope.onSomenteAtivosChange = this.action(this.#handleSomenteAtivosChange, this)
        scope.onBuscaChange = this.action(this.#handleBuscaChange, this)
        scope.onPaginaChange = this.action(this.#handlePaginaChange, this)

        scope.onAbrirBloquear = this.action(this.#handleAbrirBloquear, this)
        scope.onFecharBloquear = this.action(this.#handleFecharBloquear, this)
        scope.onTelefoneBloquearChange = this.action(this.#handleTelefoneBloquearChange, this)
        scope.onMotivoBloquearChange = this.action(this.#handleMotivoBloquearChange, this)
        scope.onConfirmarBloquear = this.action(this.#handleConfirmarBloquear, this)

        scope.onTentarNaMeta = this.action(this.#handleTentarNaMeta, this)

        scope.onAbrirDesbloquear = this.action(this.#handleAbrirDesbloquear, this)
        scope.onFecharDesbloquear = this.action(this.#handleFecharDesbloquear, this)
        scope.onMotivoDesbloquearChange = this.action(this.#handleMotivoDesbloquearChange, this)
        scope.onConfirmarDesbloquear = this.action(this.#handleConfirmarDesbloquear, this)
    }

    // :: Carga

    async #carregarTudo() {
        await Promise.all([this.#carregarSuspeitos(), this.#carregarLista()])
    }

    async #carregarSuspeitos() {
        this.scope.carregandoSuspeitos = true
        try {
            this.scope.suspeitos = await service.suspeitos()
        } catch (error) {
            this.unexpected('Carregando quem mais escreveu', error)
        } finally {
            this.scope.carregandoSuspeitos = false
        }
    }

    async #carregarLista() {
        this.scope.carregandoLista = true
        try {
            const pagina = await service.listar(
                this.scope.somenteAtivos,
                this.scope.busca.trim(),
                this.scope.pagina * BLOQUEIOS_POR_PAGINA,
                BLOQUEIOS_POR_PAGINA
            )
            this.scope.total = pagina.total
            this.scope.bloqueios = pagina.itens
        } catch (error) {
            this.unexpected('Carregando os números bloqueados', error)
        } finally {
            this.scope.carregandoLista = false
        }
    }

    async #handleSomenteAtivosChange(somenteAtivos: boolean) {
        this.scope.somenteAtivos = somenteAtivos
        this.scope.pagina = 0
        await this.#carregarLista()
    }

    async #handleBuscaChange(texto: string) {
        this.scope.busca = texto
        if (this.buscaTimer) clearTimeout(this.buscaTimer)
        this.buscaTimer = setTimeout(() => {
            this.buscaTimer = undefined
            this.scope.pagina = 0
            this.#carregarLista().catch((error) => LOG.error('Buscando bloqueios', error))
        }, DEBOUNCE_MS)
    }

    async #handlePaginaChange(pagina: number) {
        this.scope.pagina = pagina
        await this.#carregarLista()
    }

    // :: Bloquear

    /** Abre o diálogo; com um telefone, já preenchido (o "Bloquear" da lista de quem mais escreveu). */
    async #handleAbrirBloquear(telefone: string) {
        this.scope.telefoneBloquear = telefone ?? ''
        this.scope.motivoBloquear = ''
        this.scope.erroBloquear = undefined
        this.scope.bloquearAberto = true
    }

    async #handleFecharBloquear() {
        this.scope.bloquearAberto = false
    }

    async #handleTelefoneBloquearChange(texto: string) {
        this.scope.telefoneBloquear = texto
        this.scope.erroBloquear = undefined
    }

    async #handleMotivoBloquearChange(texto: string) {
        this.scope.motivoBloquear = texto
    }

    async #handleConfirmarBloquear() {
        const { scope } = this
        if (!telefoneValido(scope.telefoneBloquear) || !motivoSuficiente(scope.motivoBloquear)) return

        scope.salvando = true
        try {
            const bloqueio = await service.bloquear(scope.telefoneBloquear, scope.motivoBloquear.trim())
            scope.bloquearAberto = false
            const aviso = avisoDoBloqueio(bloqueio)
            this.app.notify(aviso.severity, aviso.texto, aviso.severity === 'warning' ? 8000 : 3000)
            await this.#carregarTudo()
        } catch (error) {
            const mensagem = mensagemDeNegocio(error)
            if (mensagem) {
                scope.erroBloquear = mensagem
            } else {
                this.unexpected('Bloqueando o número', error)
            }
        } finally {
            scope.salvando = false
        }
    }

    async #handleTentarNaMeta(telefone: string) {
        this.scope.salvando = true
        try {
            const bloqueio = await service.tentarNaMeta(telefone)
            const aviso = avisoDoBloqueio(bloqueio)
            this.app.notify(aviso.severity, aviso.texto, aviso.severity === 'warning' ? 8000 : 3000)
            await this.#carregarLista()
        } catch (error) {
            this.unexpected('Bloqueando o número na Meta', error)
        } finally {
            this.scope.salvando = false
        }
    }

    // :: Desbloquear

    async #handleAbrirDesbloquear(telefone: string) {
        this.scope.desbloquearAberto = this.scope.bloqueios.find((b) => b.telefone === telefone)
        this.scope.motivoDesbloquear = ''
    }

    async #handleFecharDesbloquear() {
        this.scope.desbloquearAberto = undefined
    }

    async #handleMotivoDesbloquearChange(texto: string) {
        this.scope.motivoDesbloquear = texto
    }

    async #handleConfirmarDesbloquear() {
        const bloqueio = this.scope.desbloquearAberto
        if (!bloqueio || !motivoSuficiente(this.scope.motivoDesbloquear)) return

        this.scope.salvando = true
        try {
            const resultado = await service.desbloquear(bloqueio.telefone, this.scope.motivoDesbloquear.trim())
            this.scope.desbloquearAberto = undefined
            this.app.notify(
                resultado.metaSituacao === 'falhou' ? 'warning' : 'success',
                resultado.metaSituacao === 'falhou'
                    ? `${formatarTelefone(bloqueio.telefone)} desbloqueado no servidor, mas a Meta recusou: ${
                          resultado.metaErro ?? 'sem detalhe'
                      }`
                    : `${formatarTelefone(bloqueio.telefone)} desbloqueado.`,
                resultado.metaSituacao === 'falhou' ? 8000 : 3000
            )
            await this.#carregarTudo()
        } catch (error) {
            this.unexpected('Desbloqueando o número', error)
        } finally {
            this.scope.salvando = false
        }
    }
}

export default BloqueiosPresenter
